import CONFIG from '../../../config';
import { debug } from '../../debug';

export const PHASE_F_STAGES = ['hydrate', 'intent', 'goal_update', 'plan', 'router', 'retrieve', 'summarize', 'sufficiency', 'refine', 'reranker', 'consolidate', 'select', 'request', 'stream', 'finalize', 'persist'];

export const STAGE_EXECUTORS = {
  hydrate: 'api.chatContext.apiLoadChatContext',
  goal_update: 'aiGoalStateHelpers.mergeGoalState',
  intent: 'stageIntent.executeIntentStage',
  plan: 'stageRetrieve.executePlanStage',
  router: 'stageRouter.executeRouterStage',
  retrieve: 'stageRetrieve.executeRetrieveStage',
  summarize: 'stageSummarize.executeSummarizeStage',
  sufficiency: 'stageSufficiency.executeSufficiencyStage',
  refine: 'stageSufficiency.executeRefineStage',
  reranker: 'stageRouter.executeRerankerStage',
  consolidate: 'stageResponse.executeConsolidateStage',
  select: 'stageResponse.executeSelectStage',
  request: 'stageResponse.executeRequestStage',
  stream: 'stageResponse.executeRequestStage',
  finalize: 'stageResponse.executeFinalizeStage',
  persist: 'stagePersist.executePersistStage'
};

// Formats simple key/value maps for stage debug summaries.
const toKeyValueList = (value = {}) => {
  if (!value || typeof value !== 'object') return '';
  const entries = Object.entries(value)
    .map(([key, count]) => `${String(key)}=${Number(count || 0)}`)
    .filter(Boolean);
  return entries.join(', ');
};

const clipLogText = (value = '', maxChars = 140) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  const limit = Math.max(40, Number(maxChars || 140));
  if (!text) return '';
  return text.length <= limit ? text : `${text.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
};

// Builds human-readable debug lines for each emitted orchestration stage event.
export const buildStageHumanSummaryLines = (event = {}) => {
  const stage = String(event?.stage || '').trim() || 'unknown';
  const stageTag = String(stage).toUpperCase();
  const withStage = (text = '') => `${stageTag} ${text}`.trim();
  const lines = [withStage(`order=${Number(event?.order || 0)} durationMs=${Number(event?.durationMs || 0)}`)];

  if (stage === 'intent') {
    lines.push(withStage(`outcome intent=${event?.intentKey || 'ask'} confidence=${Number(event?.intentConfidence || 0).toFixed(3)} source=${event?.intentSource || 'heuristic'}`));
    const contractSource = String(event?.intentContractSource || event?.contractSource || '').trim() || null;
    const contractDecisionCode = String(event?.intentContractDecisionCode || event?.contractDecisionCode || '').trim() || null;
    const contractModelAttempted = Boolean(event?.intentContractModelAttempted ?? event?.contractModelAttempted);
    if (contractSource || contractDecisionCode || contractModelAttempted) {
      lines.push(withStage(`contract source=${contractSource || 'heuristic'} decision=${contractDecisionCode || 'intent_contract_heuristic_default'} modelAttempted=${contractModelAttempted ? 'yes' : 'no'}`));
    }
    const contract = event?.intentContract && typeof event.intentContract === 'object'
      ? event.intentContract
      : null;
    const primaryKeywords = Array.isArray(contract?.primaryKeywords) ? contract.primaryKeywords.slice(0, 6) : [];
    const docHintCount = Number(event?.intentContractDocHintCount || event?.docHintCount || (Array.isArray(contract?.docHints) ? contract.docHints.length : 0) || 0);
    const budgetHintTier = String(contract?.budgetHint?.tier || event?.budgetHintTier || '').trim() || null;
    if (primaryKeywords.length > 0 || docHintCount > 0 || budgetHintTier) {
      lines.push(withStage(`contractSignals primary=${primaryKeywords.join(', ') || 'none'} docHints=${docHintCount} budgetTier=${budgetHintTier || 'n/a'}`));
    }
    const goalSummary = clipLogText(contract?.goalSummary || '', 160);
    if (goalSummary) {
      lines.push(withStage(`contractGoal="${goalSummary}"`));
    }
  }

  if (stage === 'plan') {
    const queries = Array.isArray(event?.queries) ? event.queries : [];
    lines.push(withStage(`queries(${queries.length})=${queries.join(' | ') || 'none'}`));
    lines.push(withStage(`budget items=${Number(event?.maxContextItems || 0)} chars=${Number(event?.maxContextChars || 0)} projectResults=${Number(event?.maxProjectResults || 0)}`));
  }

  if (stage === 'router' || stage === 'reranker') {
    lines.push(withStage(`outcome status=${event?.status || 'unknown'} decision=${event?.decisionCode || 'n/a'}`));
    lines.push(withStage(`confidence=${Number(event?.confidenceOverall || 0).toFixed(3)} needsReranker=${event?.needsReranker === true ? 'yes' : 'no'}`));
    lines.push(withStage(`reasons=${(Array.isArray(event?.reasonCodes) ? event.reasonCodes : []).join(', ') || 'none'}`));
  }

  if (stage === 'retrieve') {
    const reasons = Array.isArray(event?.retrievalReasons) ? event.retrievalReasons : [];
    lines.push(withStage(`outcome selected=${Number(event?.selectedCount || 0)} chars=${Number(event?.selectedChars || 0)} candidates=${Number(event?.candidateCount || 0)} iteration=${Number(event?.retrievalIteration || 1)}`));
    lines.push(withStage(`sources=${toKeyValueList(event?.selectedSourceBreakdown || event?.sourceBreakdown || {}) || 'none'}`));
    const selectedChunkIds = Array.isArray(event?.selectedChunkIds) ? event.selectedChunkIds : [];
    const fetchedChunkIds = Array.isArray(event?.fetchedChunkIds) ? event.fetchedChunkIds : [];
    lines.push(withStage(`chunks selected=${selectedChunkIds.length} fetched=${fetchedChunkIds.length}`));
    if (selectedChunkIds.length > 0) {
      lines.push(withStage(`selectedChunkIds=${selectedChunkIds.slice(0, 30).join(', ')}`));
    }
    if (fetchedChunkIds.length > 0) {
      lines.push(withStage(`fetchedChunkIds=${fetchedChunkIds.slice(0, 30).join(', ')}`));
    }
    const selectionAudit = event?.selectionAudit && typeof event.selectionAudit === 'object'
      ? event.selectionAudit
      : null;
    const selectedAudit = Array.isArray(selectionAudit?.selected) ? selectionAudit.selected : [];
    const droppedAudit = Array.isArray(selectionAudit?.dropped) ? selectionAudit.dropped : [];
    selectedAudit.slice(0, 8).forEach((entry, index) => {
      const piggyback = entry?.piggybackFromChunkId
        ? ` piggyback=${entry.piggybackReason || 'adjacent'}:${entry.piggybackFromChunkId}`
        : '';
      lines.push(
        withStage(`selected[${index + 1}] chunk=${entry?.chunkId || 'n/a'} doc=${entry?.docName || entry?.docId || 'n/a'} chars=${Number(entry?.textChars || 0)} source=${entry?.source || 'unknown'} score=${Number(entry?.score || 0).toFixed(3)} trigger=${entry?.trigger || 'n/a'} query=${entry?.query || 'n/a'}${piggyback}`)
      );
    });
    droppedAudit.slice(0, 8).forEach((entry, index) => {
      lines.push(
        withStage(`dropped[${index + 1}] chunk=${entry?.chunkId || 'n/a'} doc=${entry?.docName || entry?.docId || 'n/a'} chars=${Number(entry?.textChars || 0)} source=${entry?.source || 'unknown'} score=${Number(entry?.score || 0).toFixed(3)} trigger=${entry?.trigger || 'n/a'} reason=budget_cutoff`)
      );
    });
    const topDoc = Array.isArray(event?.selectedDocBreakdown) ? event.selectedDocBreakdown[0] : null;
    if (topDoc) {
      lines.push(withStage(`topDoc=${topDoc.docName || `doc:${topDoc.docId}`} count=${Number(topDoc.count || 0)} chars=${Number(topDoc.chars || 0)}`));
    }
    lines.push(withStage(`reasons=${reasons.join(', ') || 'none'}`));
  }

  if (stage === 'summarize') {
    lines.push(withStage(`outcome status=${event?.status || 'unknown'} decision=${event?.decisionCode || 'n/a'}`));
    lines.push(withStage(`summarizedDocs=${Number(event?.summarizedDocCount || 0)} summarizedChunks=${Number(event?.summarizedChunkCount || 0)} candidates=${Number(event?.candidateChunkCount || 0)}`));
    const docIds = Array.isArray(event?.summarizedDocIds) ? event.summarizedDocIds : [];
    const chunkHashes = Array.isArray(event?.summarizedChunkHashes) ? event.summarizedChunkHashes : [];
    if (docIds.length > 0) {
      lines.push(withStage(`docIds=${docIds.slice(0, 20).join(', ')}`));
    }
    if (chunkHashes.length > 0) {
      lines.push(withStage(`chunkHashes=${chunkHashes.slice(0, 30).join(', ')}`));
    }
    if (Array.isArray(event?.reasonCodes) && event.reasonCodes.length > 0) {
      lines.push(withStage(`reasons=${event.reasonCodes.join(', ')}`));
    }
  }

  if (stage === 'sufficiency') {
    lines.push(withStage(`outcome status=${event?.status || 'unknown'} decision=${event?.decisionCode || 'n/a'} score=${Number(event?.sufficiencyScore || 0).toFixed(3)}`));
    lines.push(withStage(`reasons=${(Array.isArray(event?.reasonCodes) ? event.reasonCodes : []).join(', ') || 'none'}`));
    const matched = Number(event?.keywordCoverage?.matchedKeywords?.length || 0);
    const missing = Number(event?.keywordCoverage?.missingKeywords?.length || 0);
    lines.push(withStage(`keywordCoverage ratio=${Number(event?.keywordCoverage?.coverageRatio || 0).toFixed(3)} matched=${matched} missing=${missing}`));
    lines.push(withStage(`exhaustiveRequested=${event?.exhaustiveRequested === true ? 'yes' : 'no'} topDocShare=${Number(event?.topDocShare || 0).toFixed(3)}`));
  }

  if (stage === 'refine') {
    lines.push(withStage(`outcome status=${event?.status || 'unknown'} decision=${event?.decisionCode || 'n/a'} retrievalIteration=${Number(event?.retrievalIteration || 1)}`));
    lines.push(withStage(`addedQueries=${(Array.isArray(event?.addedQueries) ? event.addedQueries : []).join(' | ') || 'none'}`));
  }

  if (stage === 'consolidate') {
    lines.push(withStage(`outcome status=${event?.status || 'unknown'} decision=${event?.decisionCode || 'n/a'} triggered=${event?.triggered === true ? 'yes' : 'no'}`));
    lines.push(withStage(`context beforeItems=${Number(event?.beforeContextItems || 0)} beforeChars=${Number(event?.beforeContextChars || 0)} afterItems=${Number(event?.afterContextItems || 0)} afterChars=${Number(event?.afterContextChars || 0)}`));
    lines.push(withStage(`gate minItems=${Number(event?.gateMinItems || 0)} minChars=${Number(event?.gateMinChars || 0)} maxInputItems=${Number(event?.maxInputItems || 0)} maxInputChars=${Number(event?.maxInputChars || 0)}`));
    if (event?.consolidationModel || event?.consolidationOutputChars) {
      lines.push(withStage(`model=${event?.consolidationModel || 'worker-default'} outputChars=${Number(event?.consolidationOutputChars || 0)} keptItems=${Number(event?.keptOriginalItems || 0)}`));
    }
  }

  if (stage === 'select') {
    lines.push(withStage(`outcome messages=${Number(event?.messageCount || 0)} contextCount=${Number(event?.contextCount || 0)} contextChars=${Number(event?.contextChars || 0)}`));
    const consolidatedCount = Number(event?.consolidatedContextItemCount || 0);
    const consolidatedChars = Number(event?.consolidatedContextChars || 0);
    const consolidatedInputItems = Number(event?.consolidatedSourceItemCount || 0);
    if (consolidatedCount > 0 || consolidatedChars > 0) {
      lines.push(withStage(`consolidatedContext items=${consolidatedCount} chars=${consolidatedChars} sourceItems=${consolidatedInputItems}`));
    }
  }

  if (stage === 'request' || stage === 'stream' || stage === 'finalize') {
    if (event?.candidateModel || event?.responseModel) {
      lines.push(withStage(`candidateModel=${event?.candidateModel || 'worker-default'} responseModel=${event?.responseModel || 'n/a'}`));
    }
    if (event?.usage && typeof event.usage === 'object') {
      lines.push(withStage(`usage totalTokens=${Number(event.usage.totalTokens || 0)} prompt=${Number(event.usage.promptTokens || 0)} completion=${Number(event.usage.completionTokens || 0)}`));
    }
  }

  if (stage === 'persist') {
    lines.push(withStage(`outcome goalPersist=${event?.persistStatus || 'n/a'} requestLog=${event?.requestLogStatus || 'n/a'} requestLogId=${event?.requestLogId || 'n/a'}`));
  }

  return lines;
};

// Creates the stage event emitter used for trace collection, logging, and callback notifications.
export const createStageEmitter = ({ chatTurn, onStage, collector } = {}) => {
  let order = 0;
  let lastStageTs = Number(chatTurn?.createdAt || Date.now());
  const traceStageEventsEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_TRACE_STAGE_EVENTS_ENABLED === true;

  return (stage, details = {}) => {
    if (!chatTurn?.runtime?.enabled) return;
    const normalizedStage = String(stage || '').trim().toLowerCase();
    if (!PHASE_F_STAGES.includes(normalizedStage)) return;

    order += 1;
    const tsEnd = Date.now();
    const tsStart = lastStageTs;
    const durationMs = Math.max(0, tsEnd - tsStart);
    lastStageTs = tsEnd;

    const event = {
      source: 'phase-f-stage-trace',
      requestId: chatTurn.requestId,
      stage: normalizedStage,
      executor: STAGE_EXECUTORS[normalizedStage] || 'unknown',
      order,
      status: 'ok',
      tsStart,
      tsEnd,
      durationMs,
      ...details
    };

    if (Array.isArray(collector)) {
      collector.push(event);
    }

    if (traceStageEventsEnabled) {
      debug('chat.ai.trace', { type: 'log' }, event);
    }
    const humanLines = buildStageHumanSummaryLines(event);
    humanLines.forEach((line) => {
      debug('chat.ai.stage', { type: 'log' }, line);
    });

    if (typeof onStage === 'function') {
      try {
        onStage(event);
      } catch (error) {
        debug('chat.ai.trace', { type: 'warn' }, {
          requestId: chatTurn.requestId,
          stage: normalizedStage,
          error: error?.message || 'onStage handler failed'
        });
      }
    }
  };
};
